import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SUBSCRIPTION_TIERS } from "@/lib/subscription-tiers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check, Crown, Sparkles } from "lucide-react";

const Subscribe = () => {
  const { user, subscription, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  // If already subscribed, go to dashboard
  useEffect(() => {
    if (!subscription.loading && subscription.subscribed) {
      navigate("/dashboard", { replace: true });
    }
  }, [subscription.loading, subscription.subscribed, navigate]);

  // Handle checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      refreshSubscription();
      toast.success("Subscription activated! Welcome aboard.");
      navigate("/dashboard", { replace: true });
    }
  }, [refreshSubscription, navigate]);

  const handleCheckout = async (priceId: string) => {
    setCheckoutLoading(priceId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (subscription.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-hero">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const tiers = [
    { key: "essential" as const, icon: Sparkles, popular: false },
    { key: "elite" as const, icon: Crown, popular: true },
  ];

  return (
    <div className="min-h-screen gradient-hero flex flex-col items-center justify-center px-4 py-12">
      <div className="text-center mb-10 max-w-xl">
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">
          Choose Your <span className="text-gradient-primary">Plan</span>
        </h1>
        <p className="text-muted-foreground text-lg">
          Start your 7-day free trial. Cancel anytime.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center gap-3 mb-8 bg-secondary/50 rounded-full p-1">
        <button
          onClick={() => setBillingCycle("monthly")}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
            billingCycle === "monthly"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle("yearly")}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
            billingCycle === "yearly"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Yearly
        </button>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-2 gap-6 max-w-2xl w-full">
        {tiers.map(({ key, icon: Icon, popular }) => {
          const tier = SUBSCRIPTION_TIERS[key];
          const pricing = tier[billingCycle];
          const priceId = pricing.price_id;

          return (
            <div
              key={key}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                popular
                  ? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
                  : "border-border bg-card"
              }`}
            >
              {popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-primary" />
                <h2 className="font-display text-xl font-bold">{tier.name}</h2>
              </div>

              <div className="mb-1">
                <span className="text-3xl font-bold">{pricing.label}</span>
                {billingCycle === "yearly" && "savings" in pricing && (
                  <span className="ml-2 text-sm text-green-500 font-medium">
                    {pricing.savings}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-5">7-day free trial included</p>

              <ul className="space-y-2.5 mb-6 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full"
                variant={popular ? "default" : "outline"}
                onClick={() => handleCheckout(priceId)}
                disabled={!!checkoutLoading}
              >
                {checkoutLoading === priceId ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Start Free Trial
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Subscribe;
